import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Content, CustomForm, CustomAsyncSelect } from '../../../fgs';
import { Visible, canPerform } from '../../../fgs/helpers/Authorization';
import { sizeInBytes, sizeInMegaBytes } from '../../../fgs/helpers/fileHelper';
import { Col, Input, Row } from "reactstrap";
import { FileResume } from "./components";
import { uploadForm } from "../../forms";
import { uploadValidations, uploadValidationMessages } from '../../validations';

// services
import { 
  getUserNames, 
  getUserFolders, 
  createDocumentRequest, 
  uploadChunk, 
  completeUpload,
  createDocument
} from '../../../services/api';
import RequestContext from '../../../fgs/context/RequestContext';

export const UploadDocumentPage = () => {
  const requestContext = useContext(RequestContext);
  const history = useHistory();
  const [form, setForm] = useState(uploadForm);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [defaultUserOptions, setDefaultUserOptions] = useState([]);
  const [defaultDirOptions, setDefaultDirOptions] = useState([]);

  const setData = (response, callback) => {
    if (response.data) {
      const names = response.data.map(data => {
        return {
          value: data.id,
          label: data.name
        };
      });
      callback(names);
    }
  };

  const fetchUserNames = async (inputValue, callback) => {
    await getUserNames(inputValue).then((response) => setData(response, callback));
  };

  const fetchUserFolders = async (inputValue, callback) => {
    if (!form.ownerId) return;

    await getUserFolders(form.ownerId, inputValue).then((response) => setData(response, callback));
  };

  const onChange = (name, selected) => {
    const selectedValue = selected ? selected : {};
    const newForm = Object.assign({}, form);
    newForm[name] = selectedValue;

    if (name === 'ownerId') {
      newForm.directoryId = {};
      getUserFolders(selectedValue.value).then((response) => setData(response, setDefaultDirOptions));
    }

    setForm(newForm);
  };

  const setFileStatus = (file, status) => {
    setFilesToUpload(filesToUpload.map(f => {
      if(f.order !== file.order) return f;

      f.status = status;
      return f;
    }));
  };

  const onFileSelect = (e) => {
    const files = Array.from(e.target.files).map((file, index) => {
      file.order = index;
      file.status = 'queued';
      file.uploadPercent = 0;

      return file;
    });

    const newForm = Object.assign({}, form);
    newForm.files = files;

    setForm(newForm);
    setFilesToUpload(files);
  };

  const onUpload = async () => {
    for(const file of filesToUpload) {
      setFileStatus(file, 'processing');

      if (sizeInMegaBytes(file.size) > 70) {
        await multipartUpload(file);
      } else {
        await createDocument(form, file, setFilesToUpload, filesToUpload);
      }
    }
  };

  const multipartUpload = async (file) => {
    try {
      let res = await createDocumentRequest(form, file.type, file.name);
      if(!res.data) {
        setFileStatus(file, 'failure');
        return;
      }

      let uploadId = res.data.uploadRequestId;

      await sendChunks(uploadId, file);

    } catch(e) {
      setFileStatus(file, 'failure');
    }
    
  };

  const sendChunks = async (uploadId, file) => {
    // 50MB
    let chunkSize = sizeInBytes(50);
    let chunks = Math.ceil(file.size / chunkSize, chunkSize);
    let chunk = 0;

    while (chunk < chunks) {
      let offset = chunk * chunkSize;
      let partNumber = ++chunk;


      try {
        await uploadChunk(uploadId, file.slice(offset, offset + chunkSize), partNumber);

        setFilesToUpload(filesToUpload.map(f => {
          if(f.order !== file.order) return f;
    
          f.uploadPercent = Math.round((partNumber * 100) / chunks) || 0;
          return f;
        }));

      } catch (e) {
        setFileStatus(file, 'failure');
        break;
      }
    }

    completeUpload(uploadId)
      .then(() => setFileStatus(file, 'success'))
      .catch(() => setFileStatus(file, 'failure'));
  };

  useEffect(() => {
    if (requestContext?.loggedUser?.role === 'customer') {
      fetchUserFolders(null, setDefaultDirOptions);
    } else {
      fetchUserNames(null, setDefaultUserOptions);
    }
  }, []);

  const formControls = [
    { title: 'Enviar', props: { type: 'submit', color: 'primary' } },
    { title: 'Cancelar', props: { onClick: () => history.goBack(), color: 'secondary' } }
  ];

  return (
    <Content>
      <CustomForm
        validationMessages={uploadValidationMessages}
        validations={uploadValidations}
        form={form}
        formControls={formControls}
        onSubmit={onUpload}>

        <Row>
          <Visible when={canPerform('UploadDocumentPage', 'view_all_users')}>
            <Col xs="6">
              <CustomAsyncSelect
                labelText="Usuário Destino"
                inputName="ownerId"
                defaultOptions={defaultUserOptions}
                loadOptions={fetchUserNames}
                onChange={onChange}
                selected={form.ownerId} />
            </Col>
          </Visible>
          <Col xs="6">
            <CustomAsyncSelect
              cacheOptions={form.directoryId}
              labelText="Pasta Destino"
              inputName="directoryId"
              loadOptions={fetchUserFolders}
              defaultOptions={defaultDirOptions}
              onChange={onChange}
              selected={form.directoryId} />
          </Col>
        </Row>

        <Row>
          <Col xs="6">
            <Input type="file" multiple={true} onChange={onFileSelect} />
          </Col>
        </Row>

        <FileResume files={filesToUpload} />
      </CustomForm>
    </Content>
  );
};